import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ArrowLeft, Cookie } from "lucide-react";
import { resetCookieConsent } from "@/components/CookieConsent";

const LAST_UPDATED = "February 2026";

/**
 * Privacy Policy — written to align with the EU GDPR and its Dutch
 * implementation (Uitvoeringswet AVG / UAVG). No personal email is exposed;
 * users reach out via the contact form which routes into the same inbox
 * without publishing anyone's address.
 */
export default function PrivacyPolicy() {
    return (
        <div data-testid="privacy-policy-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:py-14">
            <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-3xl p-6 sm:p-8 text-white shadow-xl"
                style={{ background: "linear-gradient(135deg, #f4a261 0%, #ee8f6d 45%, #dd6b4d 100%)" }}
            >
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-white/20 blur-2xl" aria-hidden />
                <div className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" aria-hidden />
                <div className="relative flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/95 text-primary flex items-center justify-center shadow-lg shrink-0">
                        <Shield className="w-7 h-7" strokeWidth={2.2} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/80">Legal</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight">Privacy Policy</h1>
                        <div className="text-white/85 text-xs sm:text-sm mt-1">Last updated: {LAST_UPDATED}</div>
                    </div>
                </div>
            </motion.section>

            <div className="mt-6">
                <Link
                    to="/"
                    data-testid="privacy-back-link"
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to home
                </Link>
            </div>

            <div className="mt-6 space-y-8 text-[15px] leading-relaxed text-foreground/90">
                <p>
                    ErasmusSwap ("we", "our", "us") is a free, non-commercial platform run from the Netherlands that connects
                    Erasmus and other exchange students so they can swap rooms or find an Exchange Buddy in their destination city.
                    This Privacy Policy explains what personal data we collect, why we collect it, how long we keep it, and what
                    rights you have under the EU General Data Protection Regulation (GDPR / AVG) and its Dutch implementation, the
                    Uitvoeringswet AVG (UAVG).
                </p>
                <p className="text-sm text-muted-foreground">
                    You have the right to know how we handle your data. If any part of this policy is unclear, please{" "}
                    <ContactLink /> — we're happy to explain.
                </p>

                <Section title="1. Data controller">
                    <p>
                        ErasmusSwap is the data controller within the meaning of GDPR art. 4(7). We are a small student-run project
                        based in the Netherlands and can be reached through the <ContactLink /> — this replaces publishing any
                        personal email address online. Because we process only limited data on a small scale, we are not required
                        to appoint a formal Data Protection Officer under GDPR art. 37, but privacy questions still receive a reply
                        within 30 days.
                    </p>
                </Section>

                <Section title="2. What data we collect">
                    <p>We only collect data that is necessary to make the platform work:</p>
                    <ul className="list-disc pl-6 space-y-1.5">
                        <li><strong>Account data</strong>: name, email address, hashed password.</li>
                        <li><strong>Profile data (optional)</strong>: home city, home university, exchange destination, bio, avatar.</li>
                        <li><strong>Listing data</strong>: room title, description, city, country, university, rent, currency,
                            available dates, destination city, amenities, photos you upload.</li>
                        <li><strong>Messages</strong>: the content of messages you send to other users through the in-app chat.</li>
                        <li><strong>Technical data</strong>: IP address at the time of login, browser type, device type — used for
                            anti-abuse and security only.</li>
                        <li><strong>Cookies / local storage</strong>: an authentication token so you stay logged in, plus a small
                            set of preference flags (e.g. whether you've seen the onboarding). These are strictly-necessary for the
                            service to function and do not require prior consent under art. 11.7a Telecommunicatiewet.</li>
                        <li><strong>Analytics (optional, opt-in)</strong>: aggregated, non-identifying page-view statistics via
                            Google Analytics with IP anonymisation. This is <strong>only loaded after you actively accept</strong>{" "}
                            on the cookie banner. If you reject or ignore the banner, no analytics scripts run and no analytics
                            cookies are set. You can change your choice at any time — see section 12 below.</li>
                    </ul>
                    <p className="text-sm text-muted-foreground">
                        We <strong>do not</strong> collect passport numbers, dates of birth, payment information, or any special
                        category data (health, religion, sexual orientation, ethnicity, etc.). We <strong>do not</strong> sell your
                        data. We <strong>do not</strong> profile users for advertising purposes. We <strong>do not</strong> make
                        automated decisions that produce legal or similarly significant effects on you (GDPR art. 22).
                    </p>
                </Section>

                <Section title="3. Why we use your data (legal bases)">
                    <ul className="list-disc pl-6 space-y-1.5">
                        <li><strong>To provide the service</strong> — matching, chat, listings, account management (GDPR
                            art. 6(1)(b): performance of a contract).</li>
                        <li><strong>To notify you</strong> about new matches, replies, and password resets (GDPR art. 6(1)(b):
                            necessary to deliver the service you signed up for).</li>
                        <li><strong>To keep the platform safe</strong> — abuse prevention, spam blocking, admin moderation
                            (GDPR art. 6(1)(f): our legitimate interest in a safe community).</li>
                        <li><strong>To comply with legal obligations</strong> when we receive a lawful request (GDPR art. 6(1)(c)).</li>
                        <li><strong>Analytics</strong> — only after you have opted in via the cookie banner, based on your
                            consent (GDPR art. 6(1)(a)). You can withdraw consent at any time in section 12.</li>
                    </ul>
                </Section>

                <Section title="4. Who we share your data with">
                    <p>
                        Your data is shared only with the following processors, each bound by a data processing agreement
                        (verwerkersovereenkomst) under GDPR art. 28, and only for the purposes listed above:
                    </p>
                    <ul className="list-disc pl-6 space-y-1.5">
                        <li><strong>Resend</strong> — sends transactional emails (password reset, match notifications). Only your
                            email address, name and the message body are transmitted.</li>
                        <li><strong>MongoDB Atlas / hosting provider</strong> — stores the database and uploaded photos.</li>
                        <li><strong>Emergent / Kubernetes hosting</strong> — runs the servers.</li>
                        <li><strong>Wikimedia Commons</strong> — we fetch public city photos through Wikipedia's API as fallbacks;
                            no personal data is sent.</li>
                        <li><strong>Google Analytics</strong> — where enabled, aggregated page views only.</li>
                    </ul>
                    <p>
                        Other users of the platform can see the parts of your profile and listing that you have chosen to publish
                        (name, city, destination, room details, photos). Your <strong>email address is never shown</strong> to
                        other users — they contact you only via the in-app chat.
                    </p>
                </Section>

                <Section title="5. International transfers">
                    <p>
                        Our servers are located in the European Economic Area whenever possible. Some processors (e.g. Google
                        Analytics) may transfer data to the United States. In those cases we rely on the Standard Contractual
                        Clauses approved by the European Commission (GDPR art. 46(2)(c)) or equivalent safeguards, and only the
                        minimum necessary data is transferred.
                    </p>
                </Section>

                <Section title="6. How long we keep your data">
                    <ul className="list-disc pl-6 space-y-1.5">
                        <li><strong>Account data</strong>: as long as your account is active. Delete your account at any time via
                            <em> Profile → Danger zone</em> — this permanently removes your profile, rooms, messages, and uploaded
                            photos.</li>
                        <li><strong>Listings</strong>: until you remove them or delete your account.</li>
                        <li><strong>Messages</strong>: until either party deletes their account.</li>
                        <li><strong>Server / login logs</strong>: max 30 days for security purposes, then automatically rotated
                            out.</li>
                    </ul>
                </Section>

                <Section title="7. Your rights">
                    <p>Under the GDPR / AVG you have the right to:</p>
                    <ul className="list-disc pl-6 space-y-1.5">
                        <li>Access the personal data we hold about you (art. 15)</li>
                        <li>Correct data that is inaccurate (art. 16) — edit it in your profile, or ask us via the contact form</li>
                        <li>Delete your account and associated data ("right to be forgotten", art. 17)</li>
                        <li>Restrict processing (art. 18)</li>
                        <li>Object to processing based on our legitimate interest (art. 21)</li>
                        <li>Data portability — request a copy of your data in a machine-readable format (art. 20)</li>
                        <li>Withdraw any consent you gave earlier (art. 7(3))</li>
                        <li>Not be subject to purely automated decision-making (art. 22)</li>
                        <li>Lodge a complaint with the Dutch Data Protection Authority — Autoriteit Persoonsgegevens{" "}
                            (<a href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer"
                                className="text-primary hover:underline">autoriteitpersoonsgegevens.nl</a>)
                        </li>
                    </ul>
                    <p>
                        The fastest way to exercise most of these rights is inside the app: <em>Profile</em> lets you view and
                        update your data; the <em>Danger zone</em> button deletes everything permanently. For anything else,
                        <ContactLink prefix=" " suffix=" " /> and we'll reply within 30 days as required by GDPR art. 12(3).
                    </p>
                </Section>

                <Section title="8. Security">
                    <p>
                        We take appropriate technical and organisational measures as required by GDPR art. 32:
                    </p>
                    <ul className="list-disc pl-6 space-y-1.5">
                        <li>Passwords are stored hashed with bcrypt — plaintext is never stored or logged.</li>
                        <li>All traffic between your device and our servers is encrypted with TLS (HTTPS).</li>
                        <li>Uploaded photos are served over HTTPS and stored in access-controlled object storage.</li>
                        <li>Principle of least privilege — only designated admins have direct database access, and access is
                            logged.</li>
                        <li>Automated rate-limiting protects against brute-force login attempts.</li>
                    </ul>
                </Section>

                <Section title="9. Data breaches">
                    <p>
                        In the unlikely event of a personal data breach that is likely to result in a risk to your rights and
                        freedoms, we will notify the Autoriteit Persoonsgegevens within 72 hours of becoming aware of it, as
                        required by GDPR art. 33. If the breach is likely to result in a <em>high</em> risk to you, we will also
                        notify you directly and without undue delay, as required by GDPR art. 34.
                    </p>
                </Section>

                <Section title="10. Children">
                    <p>
                        ErasmusSwap is intended for university students, typically aged 18+. Under the UAVG, minors under 16 cannot
                        give valid consent for online services without a parent or guardian, so we do not knowingly collect data
                        from anyone under 16. If you believe we have data about a minor, please <ContactLink /> and we will delete
                        it.
                    </p>
                </Section>

                <Section title="11. Changes to this policy">
                    <p>
                        We may update this policy from time to time to reflect changes in the service or in the law. When we make a
                        material change, we will note it here and — if it affects you meaningfully — send you a notification email.
                        The date at the top of this page always reflects the most recent version.
                    </p>
                </Section>

                <Section title="12. Cookie preferences">
                    <p>
                        You accepted or rejected our analytics cookies once via the banner at the bottom of the page. You can
                        change that decision at any time — clicking the button below clears your saved choice and reloads the site
                        so the banner appears again.
                    </p>
                    <button
                        type="button"
                        data-testid="privacy-manage-cookies-btn"
                        onClick={resetCookieConsent}
                        className="inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold bg-foreground text-background hover:bg-foreground/90 transition-colors"
                    >
                        <Cookie className="w-4 h-4" /> Manage cookie preferences
                    </button>
                </Section>

                <Section title="13. Contact">
                    <p>
                        Questions, requests, or complaints about this policy or how we handle your data?{" "}
                        <ContactLink capitalize /> — we won't publish any personal email address here, but every message is read
                        and replied to by a real person within 30 days.
                    </p>
                </Section>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="space-y-3">
            <h2 className="font-display text-xl sm:text-2xl font-semibold">{title}</h2>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function ContactLink({ capitalize = false, prefix = "", suffix = "" }) {
    return (
        <>
            {prefix}
            <Link to="/contact" className="text-primary hover:underline font-medium" data-testid="privacy-contact-link">
                {capitalize ? "Please use our contact form" : "please use our contact form"}
            </Link>
            {suffix}
        </>
    );
}
